import { useMemo, useState } from "react";
import { useRoute, Link } from "wouter";
import Layout from "@/components/layout/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAgencyContext } from "@/lib/agencyContext";
import { useInsuredPolicies, useInsured, useInsuredChangeHistory, type InsuredChangeHistoryEntry } from "@/lib/api";
import { cn } from "@/lib/utils";
import { formatDate, formatDollar } from "@/lib/formatters";
import { insuredFieldLabel } from "@/lib/insured-field-labels";
import { getDriverDisplayName } from "@/lib/driver-display";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  aggregateDrivers,
  aggregateTrailers,
  aggregateVehicles,
  isNonOwnedAttachedTrailer,
} from "@/components/insured/assets/asset-helpers"; // CSR-776
import { formatPolicyStatusLabel } from "@/components/insured/policies/policy-status";
import {
  ArrowLeft,
  Loader2,
  FileText,
  Mail,
  Truck,
  ShieldAlert,
  AlertTriangle,
  ClipboardCheck,
  Users,
  CarFront,
  Pencil,
  RotateCcw,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import type { InspectionSummary, CrashSummary, Violation, BasicScores } from "@shared/schema";
import { IN_FORCE_STATUSES } from "@shared/schema"; // CSR-1800: use shared constant (excludes "renewal offer" — renewal offers are Inactive)
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";

function getStatusBadgeClass(status?: string): string {
  switch (status?.toLowerCase()) {
    case "active":
      return "bg-green-100 text-green-800 border-green-200";
    case "pending cancel":
      return "bg-orange-100 text-orange-800 border-orange-200";
    case "renewing":
      return "bg-blue-100 text-blue-800 border-blue-200";
    case "reinstated":
      return "bg-teal-100 text-teal-800 border-teal-200";
    case "non-renewal":
      return "bg-amber-100 text-amber-800 border-amber-200";
    case "expired":
      return "bg-gray-100 text-gray-800 border-gray-200";
    case "renewed":
      return "bg-indigo-100 text-indigo-800 border-indigo-200";
    case "cancelled":
      return "bg-red-100 text-red-800 border-red-200";
    case "replaced":
      return "bg-purple-100 text-purple-800 border-purple-200";
    default:
      return "bg-gray-100 text-gray-600 border-gray-200";
  }
}

function formatCurrency(value?: string | null): string {
  if (!value) return "$0";
  return `$${Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
}

export default function InsuredDetail() {
  const [, params] = useRoute("/insureds-old/:id");
  const insuredId = params?.id || "";
  const { data: insuredFromId, isLoading: insuredLoading } = useInsured(insuredId);
  const customerName = insuredFromId?.name || insuredFromId?.commercialName || "";
  const { selectedAgency, isAllAgencies } = useAgencyContext();
  const agencyId = isAllAgencies ? undefined : selectedAgency?.id;
  // CSR-674: dedicated /api/insureds/:id/policies endpoint replaces the
  // bulk-list path. This is the legacy `/insureds-old/:id` route — kept
  // and patched per CSR-669 commit 9d0723f.
  const { data: policiesData, isLoading: policiesLoading } = useInsuredPolicies(
    insuredFromId?.id ?? null,
    agencyId,
  );
  const isLoading = insuredLoading || policiesLoading;
  const isMobile = useIsMobile();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const insuredPolicies = useMemo(() => policiesData?.data ?? [], [policiesData?.data]);

  // CSR-776: dedupe + lifecycle-sort vehicles, trailers, and drivers via the
  // shared aggregator so removed assets sort to the bottom and removedAt is
  // tracked. Combine power units + trailers for the existing Fleet render loop.
  const fleetPowerUnits = useMemo(() => aggregateVehicles(insuredPolicies), [insuredPolicies]);
  const fleetTrailers = useMemo(() => aggregateTrailers(insuredPolicies), [insuredPolicies]);
  const fleetVehicles = useMemo(
    () => [...fleetPowerUnits, ...fleetTrailers],
    [fleetPowerUnits, fleetTrailers],
  );
  const companyDrivers = useMemo(() => aggregateDrivers(insuredPolicies), [insuredPolicies]);

  // Pull DOT number early so we can use it as a fallback for insured lookup
  const dotNumber = useMemo(() => {
    return insuredPolicies.find((p) => p.dotNumber)?.dotNumber || null;
  }, [insuredPolicies]);

  const insuredProfile = insuredFromId;

  const inspectionSummary = insuredProfile?.inspectionSummary as InspectionSummary | null;
  const crashSummary = insuredProfile?.crashSummary as CrashSummary | null;
  const violations = insuredProfile?.violations as Violation[] | null;
  const basicScores = insuredProfile?.basicScores as BasicScores | null;

  const sortedPolicies = useMemo(() => {
    return [...insuredPolicies].sort((a, b) =>
      (b.expirationDate || "").localeCompare(a.expirationDate || "")
    );
  }, [insuredPolicies]);

  const stats = useMemo(() => {
    const now = new Date();
    const todayMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();

    const inForcePolicies = insuredPolicies.filter((p) =>
      IN_FORCE_STATUSES.includes(
        (p.status?.toLowerCase() || "") as (typeof IN_FORCE_STATUSES)[number],
      ),
    ).length;
    const inForcePremium = insuredPolicies
      .filter((p) => {
        if (!p.effectiveDate || !p.expirationDate) return false;
        if (
          !IN_FORCE_STATUSES.includes(
            (p.status?.toLowerCase() ?? "") as (typeof IN_FORCE_STATUSES)[number],
          )
        )
          return false;
        const effDate = new Date(p.effectiveDate + "T00:00:00").getTime();
        const expDate = new Date(p.expirationDate + "T00:00:00").getTime();
        return effDate <= todayMidnight && expDate >= todayMidnight;
      })
      .reduce((sum, p) => sum + Number(p.premium || 0), 0);
    const parentCarriers = Array.from(
      new Set(insuredPolicies.map((p) => p.parentCarrier).filter(Boolean)),
    );
    return { inForcePolicies, inForcePremium, parentCarriers };
  }, [insuredPolicies]);

  const companyEmail = useMemo(() => {
    return insuredPolicies.find((p) => p.companyEmail)?.companyEmail || null;
  }, [insuredPolicies]);

  const { data: changeHistory = [] } = useInsuredChangeHistory(insuredProfile?.id || null);
  const [changeHistoryExpanded, setChangeHistoryExpanded] = useState(true);

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }

  if (!isLoading && !insuredFromId) {
    return (
      <Layout>
        <div className="space-y-6">
          <Link href="/insureds">
            <Button
              variant="ghost"
              className="gap-2 pl-0 hover:pl-2 transition-all"
            >
              <ArrowLeft className="w-4 h-4" /> Back to Insureds
            </Button>
          </Link>
          <Card>
            <CardContent className="p-12 text-center">
              <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
              <p className="text-lg font-medium">Insured not found</p>
              <p className="text-sm text-muted-foreground mt-1">
                No policies found for this insured, or the insured does not
                exist.
              </p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="space-y-2">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
            <div className="flex items-center gap-3">
              <Link href="/insureds">
                <Button variant="ghost" size="icon" className="shrink-0">
                  <ArrowLeft className="w-4 h-4" />
                </Button>
              </Link>
              <div>
                <h1 className="text-2xl font-bold tracking-tight">
                  {customerName}
                </h1>
                {insuredFromId?.dbaName && (
                  <p className="text-sm text-muted-foreground">DBA: {insuredFromId.dbaName}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {dotNumber && (
                <Badge
                  variant="outline"
                  className="gap-1 font-mono text-xs"
                >
                  <Truck className="w-3 h-3" />
                  DOT {dotNumber}
                </Badge>
              )}
              {companyEmail && (
                <Badge
                  variant="outline"
                  className="gap-1 text-xs text-muted-foreground"
                >
                  <Mail className="w-3 h-3" />
                  {companyEmail}
                </Badge>
              )}
              {insuredProfile?.id && (
                <Link href={`/insureds/${insuredProfile.id}/edit`}>
                  <Button variant="outline" size="sm" className="gap-1.5">
                    <Pencil className="w-3.5 h-3.5" /> Edit
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>

        {/* Company Profile */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Company Profile</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4">
              <div>
                <p className="text-xs text-muted-foreground">DBA / Commercial Name</p>
                <p className="text-sm font-medium">{insuredProfile?.commercialName || "--"}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">MC Number</p>
                <p className="text-sm font-medium font-mono">{insuredProfile?.mcNumber || "--"}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">FMCSA Status</p>
                {insuredProfile?.fmcsaStatusCode ? (
                  <Badge variant="outline" className={cn(
                    "text-xs",
                    insuredProfile.fmcsaStatusCode === "A"
                      ? "bg-green-50 text-green-700 border-green-200"
                      : "bg-red-50 text-red-700 border-red-200"
                  )}>
                    {insuredProfile.fmcsaStatusCode === "A" ? "Authorized" : insuredProfile.fmcsaStatusCode}
                  </Badge>
                ) : (
                  <p className="text-sm font-medium">--</p>
                )}
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Email</p>
                <p className="text-sm font-medium">
                  {insuredProfile?.email ? (
                    <a href={`mailto:${insuredProfile.email}`} className="text-primary hover:underline cursor-pointer">
                      {insuredProfile.email}
                    </a>
                  ) : "--"}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Phone</p>
                <p className="text-sm font-medium">{insuredProfile?.telephone || "--"}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Fax</p>
                <p className="text-sm font-medium">{insuredProfile?.fax || "--"}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Business Type</p>
                <p className="text-sm font-medium">{insuredProfile?.businessOrg || "--"}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Company Officer</p>
                <p className="text-sm font-medium">{insuredProfile?.companyOfficer || "--"}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Fleet</p>
                <p className="text-sm font-medium">
                  {[
                    insuredProfile?.totalDrivers != null && `${insuredProfile.totalDrivers} ${insuredProfile.totalDrivers === 1 ? "driver" : "drivers"}`,
                    insuredProfile?.totalPowerUnits != null && `${insuredProfile.totalPowerUnits} ${insuredProfile.totalPowerUnits === 1 ? "power unit" : "power units"}`,
                  ].filter(Boolean).join(", ") || "--"}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Sources</p>
                {(() => {
                  if (!insuredProfile?.sources) return <p className="text-sm font-medium">--</p>;
                  let items: string[] = [];
                  try {
                    const parsed = JSON.parse(insuredProfile.sources);
                    items = Array.isArray(parsed) ? parsed : [];
                  } catch {
                    items = insuredProfile.sources.split(",");
                  }
                  items = items.map((s: string) => s.trim()).filter(Boolean);
                  if (items.length > 0) {
                    return (
                      <div className="flex flex-wrap gap-1 mt-0.5">
                        {items.map((s: string) => (
                          <Badge key={s} variant="secondary" className="text-xs">{s}</Badge>
                        ))}
                      </div>
                    );
                  }
                  return <p className="text-sm font-medium">--</p>;
                })()}
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Physical Address</p>
                {insuredProfile?.phyStreet ? (
                  <p className="text-sm font-medium">
                    {insuredProfile.phyStreet}
                    <br />
                    {[insuredProfile.phyCity, insuredProfile.phyState, insuredProfile.phyZip].filter(Boolean).join(", ")}
                    {insuredProfile.phyCountry && insuredProfile.phyCountry !== "US" && (
                      <>, {insuredProfile.phyCountry}</>
                    )}
                  </p>
                ) : (
                  <p className="text-sm font-medium">--</p>
                )}
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Mailing Address</p>
                {(() => {
                  if (!insuredProfile?.mailingStreet) return <p className="text-sm font-medium">--</p>;
                  const isSame = insuredProfile.mailingStreet === insuredProfile.phyStreet
                    && insuredProfile.mailingCity === insuredProfile.phyCity
                    && insuredProfile.mailingState === insuredProfile.phyState
                    && insuredProfile.mailingZip === insuredProfile.phyZip;
                  if (isSame) {
                    return <p className="text-sm font-medium text-muted-foreground">Same as physical address</p>;
                  }
                  return (
                    <p className="text-sm font-medium">
                      {insuredProfile.mailingStreet}
                      <br />
                      {[insuredProfile.mailingCity, insuredProfile.mailingState, insuredProfile.mailingZip].filter(Boolean).join(", ")}
                      {insuredProfile.mailingCountry && insuredProfile.mailingCountry !== "US" && (
                        <>, {insuredProfile.mailingCountry}</>
                      )}
                    </p>
                  );
                })()}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-sidebar text-sidebar-foreground border-none shadow-lg">
            <CardContent className="p-6">
              <div className="text-sidebar-primary font-medium text-sm mb-1">In Force Premium</div>
              <div className="text-3xl font-bold">
                {formatCurrency(stats.inForcePremium.toString())}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="text-muted-foreground font-medium text-sm mb-1">In Force Policies</div>
              <div className="text-3xl font-bold text-green-600">{stats.inForcePolicies}</div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="text-muted-foreground font-medium text-sm mb-1">Parent Carriers</div>
              {stats.parentCarriers.length === 0 ? (
                <div className="text-3xl font-bold">--</div>
              ) : stats.parentCarriers.length <= 3 ? (
                <div className="flex flex-wrap gap-1 mt-1">
                  {stats.parentCarriers.map((c) => (
                    <Badge key={c} variant="secondary" className="text-xs">
                      {c}
                    </Badge>
                  ))}
                </div>
              ) : (
                <div>
                  <div className="text-3xl font-bold">{stats.parentCarriers.length}</div>
                  <div className="text-xs text-muted-foreground truncate mt-1">
                    {stats.parentCarriers.slice(0, 3).join(", ")}
                    {stats.parentCarriers.length > 3 &&
                      ` +${stats.parentCarriers.length - 3} more`}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Policies Table / Mobile Cards */}
        <Card>
          <CardHeader>
            <CardTitle>Policies</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {isMobile ? (
              /* Mobile card view */
              <div className="divide-y">
                {sortedPolicies.map((policy) => (
                  <div
                    key={policy.id}
                    className="p-4 cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => navigate(`/policies/${policy.id}`)}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-mono font-medium text-primary text-sm">
                          {policy.policyNumber}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {policy.type || "--"}
                        </p>
                      </div>
                      <Badge
                        variant="outline"
                        className={cn(
                          "text-xs",
                          getStatusBadgeClass(policy.status),
                        )}
                      >
                        {formatPolicyStatusLabel(policy.status)}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                      <div>
                        <span className="text-muted-foreground text-xs">
                          Carrier
                        </span>
                        <p className="truncate">{policy.carrier}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground text-xs">
                          Producer
                        </span>
                        <p className="truncate">
                          {policy.producer?.split(",")[0]?.trim() || "--"}
                        </p>
                      </div>
                      <div>
                        <span className="text-muted-foreground text-xs">
                          Effective
                        </span>
                        <p>{formatDate(policy.effectiveDate)}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground text-xs">
                          Expiration
                        </span>
                        <p>{formatDate(policy.expirationDate)}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground text-xs">
                          Premium
                        </span>
                        <p>
                          {policy.premium
                            ? `$${Number(policy.premium).toLocaleString()}`
                            : "--"}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              /* Desktop table view */
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Policy Number</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Carrier</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Producer</TableHead>
                      <TableHead>Effective Date</TableHead>
                      <TableHead>Expiration Date</TableHead>
                      <TableHead>Premium</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedPolicies.map((policy) => (
                      <TableRow
                        key={policy.id}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => navigate(`/policies/${policy.id}`)}
                      >
                        <TableCell className="font-mono font-medium text-primary">
                          {policy.policyNumber}
                        </TableCell>
                        <TableCell>{policy.type || "--"}</TableCell>
                        <TableCell>{policy.carrier}</TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={cn(getStatusBadgeClass(policy.status))}
                          >
                            {formatPolicyStatusLabel(policy.status)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {policy.producer?.split(",")[0]?.trim() || "--"}
                        </TableCell>
                        <TableCell>{formatDate(policy.effectiveDate)}</TableCell>
                        <TableCell>
                          {formatDate(policy.expirationDate)}
                        </TableCell>
                        <TableCell>
                          {policy.premium
                            ? `$${Number(policy.premium).toLocaleString()}`
                            : "--"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Fleet Vehicles */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Truck className="w-5 h-5" />
              Fleet Vehicles
              {fleetVehicles.length > 0 && (
                <Badge variant="secondary" className="text-xs ml-1">
                  {fleetVehicles.filter(fv => fv.active).length} active / {fleetVehicles.length} total
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {fleetVehicles.length === 0 ? (
              <p className="text-sm text-muted-foreground">No vehicle data available across policies</p>
            ) : isMobile ? (
              <div className="space-y-3">
                {fleetVehicles.map((fv, i) => (
                  <div key={fv.vehicle.vin || i} className={cn("border rounded-lg p-3 space-y-1", !fv.active && "opacity-60")}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">
                          {[isNonOwnedAttachedTrailer(fv.vehicle) ? undefined : fv.vehicle.year, fv.vehicle.make, fv.vehicle.model].filter(Boolean).join(" ") || fv.vehicle.truckType || "Unknown"}
                        </span>
                        <Badge
                          variant="outline"
                          className={cn("text-xs", fv.active
                            ? "bg-green-50 text-green-700 border-green-200"
                            : "bg-gray-50 text-gray-500 border-gray-200"
                          )}
                        >
                          {fv.active ? "Active" : "Inactive"}
                        </Badge>
                      </div>
                      {fv.vehicle.truckType && (
                        <Badge variant="outline" className="text-xs">{fv.vehicle.truckType}</Badge>
                      )}
                    </div>
                    {fv.vehicle.vin && !isNonOwnedAttachedTrailer(fv.vehicle) && (
                      <p className="text-xs font-mono text-muted-foreground">{fv.vehicle.vin}</p>
                    )}
                    <Link href={`/policies/${fv.policyId}`}>
                      <span className="text-xs text-primary hover:underline cursor-pointer">{fv.policyNumber}</span>
                    </Link>
                  </div>
                ))}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-2 pr-4 text-xs font-medium text-muted-foreground">Status</th>
                      <th className="text-left py-2 pr-4 text-xs font-medium text-muted-foreground">Year</th>
                      <th className="text-left py-2 pr-4 text-xs font-medium text-muted-foreground">Make</th>
                      <th className="text-left py-2 pr-4 text-xs font-medium text-muted-foreground">Model</th>
                      <th className="text-left py-2 pr-4 text-xs font-medium text-muted-foreground">Type</th>
                      <th className="text-left py-2 pr-4 text-xs font-medium text-muted-foreground">VIN</th>
                      <th className="text-left py-2 text-xs font-medium text-muted-foreground">Policy</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fleetVehicles.map((fv, i) => (
                      <tr key={fv.vehicle.vin || i} className={cn("border-b border-border/50", !fv.active && "opacity-60")}>
                        <td className="py-2 pr-4">
                          <Badge
                            variant="outline"
                            className={cn("text-xs", fv.active
                              ? "bg-green-50 text-green-700 border-green-200"
                              : "bg-gray-50 text-gray-500 border-gray-200"
                            )}
                          >
                            {fv.active ? "Active" : "Inactive"}
                          </Badge>
                        </td>
                        <td className="py-2 pr-4 text-xs font-medium">{isNonOwnedAttachedTrailer(fv.vehicle) ? "—" : (fv.vehicle.year || "—")}</td>
                        <td className="py-2 pr-4 text-xs font-medium">{fv.vehicle.make || "—"}</td>
                        <td className="py-2 pr-4 text-xs font-medium">{fv.vehicle.model || "—"}</td>
                        <td className="py-2 pr-4 text-xs font-medium">{fv.vehicle.truckType || "—"}</td>
                        <td className="py-2 pr-4 text-xs font-mono text-muted-foreground">{isNonOwnedAttachedTrailer(fv.vehicle) ? "—" : (fv.vehicle.vin || "—")}</td>
                        <td className="py-2 text-xs">
                          <Link href={`/policies/${fv.policyId}`}>
                            <span className="text-primary hover:underline cursor-pointer">{fv.policyNumber}</span>
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Company Drivers */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="w-5 h-5" />
              Company Drivers
              {companyDrivers.length > 0 && (
                <Badge variant="secondary" className="text-xs ml-1">
                  {companyDrivers.filter(cd => cd.active).length} active / {companyDrivers.length} total
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {companyDrivers.length === 0 ? (
              <p className="text-sm text-muted-foreground">No driver data available across policies</p>
            ) : isMobile ? (
              <div className="space-y-3">
                {companyDrivers.map((cd, i) => (
                  <div key={cd.driver.licenseNumber || i} className={cn("border rounded-lg p-3 space-y-1", !cd.active && "opacity-60")}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">
                          {getDriverDisplayName(cd.driver)}
                        </span>
                        <Badge
                          variant="outline"
                          className={cn("text-xs", cd.active
                            ? "bg-green-50 text-green-700 border-green-200"
                            : "bg-gray-50 text-gray-500 border-gray-200"
                          )}
                        >
                          {cd.active ? "Active" : "Inactive"}
                        </Badge>
                      </div>
                      {cd.driver.licenseState && (
                        <Badge variant="outline" className="text-xs">{cd.driver.licenseState}</Badge>
                      )}
                    </div>
                    {cd.driver.licenseNumber && (
                      <p className="text-xs font-mono text-muted-foreground">{cd.driver.licenseNumber}</p>
                    )}
                    <Link href={`/policies/${cd.policyId}`}>
                      <span className="text-xs text-primary hover:underline cursor-pointer">{cd.policyNumber}</span>
                    </Link>
                  </div>
                ))}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-2 pr-4 text-xs font-medium text-muted-foreground">Status</th>
                      <th className="text-left py-2 pr-4 text-xs font-medium text-muted-foreground">Name</th>
                      <th className="text-left py-2 pr-4 text-xs font-medium text-muted-foreground">License #</th>
                      <th className="text-left py-2 pr-4 text-xs font-medium text-muted-foreground">State</th>
                      <th className="text-left py-2 pr-4 text-xs font-medium text-muted-foreground">DOB</th>
                      <th className="text-left py-2 pr-4 text-xs font-medium text-muted-foreground">Experience</th>
                      <th className="text-left py-2 text-xs font-medium text-muted-foreground">Policy</th>
                    </tr>
                  </thead>
                  <tbody>
                    {companyDrivers.map((cd, i) => (
                      <tr key={cd.driver.licenseNumber || i} className={cn("border-b border-border/50", !cd.active && "opacity-60")}>
                        <td className="py-2 pr-4">
                          <Badge
                            variant="outline"
                            className={cn("text-xs", cd.active
                              ? "bg-green-50 text-green-700 border-green-200"
                              : "bg-gray-50 text-gray-500 border-gray-200"
                            )}
                          >
                            {cd.active ? "Active" : "Inactive"}
                          </Badge>
                        </td>
                        <td className="py-2 pr-4 text-xs font-medium">{getDriverDisplayName(cd.driver)}</td>
                        <td className="py-2 pr-4 text-xs font-mono text-muted-foreground">{cd.driver.licenseNumber || "—"}</td>
                        <td className="py-2 pr-4 text-xs font-medium">{cd.driver.licenseState || "—"}</td>
                        <td className="py-2 pr-4 text-xs font-medium">{cd.driver.dateOfBirth ? formatDate(cd.driver.dateOfBirth, { dateOnly: true }) : "—"}</td>
                        <td className="py-2 pr-4 text-xs font-medium">{cd.driver.yearsExperience != null ? `${cd.driver.yearsExperience} yrs` : "—"}</td>
                        <td className="py-2 text-xs">
                          <Link href={`/policies/${cd.policyId}`}>
                            <span className="text-primary hover:underline cursor-pointer">{cd.policyNumber}</span>
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* BASIC Safety Scores */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <ShieldAlert className="w-5 h-5" />
                BASIC Safety Scores
              </CardTitle>
              {basicScores?.asOfDate && (
                <span className="text-xs text-muted-foreground">
                  As of {formatDate(basicScores.asOfDate)}
                </span>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
              {([
                ["Unsafe Driving", basicScores?.unsafeDriving ?? null],
                ["HOS", basicScores?.hoursOfService ?? null],
                ["Vehicle Maint.", basicScores?.vehicleMaintenance ?? null],
                ["Ctrl. Substances", basicScores?.controlledSubstances ?? null],
                ["Haz. Materials", basicScores?.hazardousMaterials ?? null],
                ["Driver Fitness", basicScores?.driverFitness ?? null],
                ["Crash Indicator", basicScores?.crashIndicator ?? null],
              ] as [string, number | null][]).map(([label, score]) => (
                <div
                  key={label}
                  className={cn(
                    "rounded-lg border p-3 text-center",
                    score === null
                      ? "bg-gray-50 border-gray-200"
                      : score >= 75
                        ? "bg-red-50 border-red-200"
                        : score >= 50
                          ? "bg-yellow-50 border-yellow-200"
                          : "bg-green-50 border-green-200",
                  )}
                >
                  <p className="text-xs text-muted-foreground mb-1 truncate">{label}</p>
                  <p
                    className={cn(
                      "text-2xl font-bold",
                      score === null
                        ? "text-gray-400"
                        : score >= 75
                          ? "text-red-700"
                          : score >= 50
                            ? "text-yellow-700"
                            : "text-green-700",
                    )}
                  >
                    {score !== null ? score : "N/A"}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Inspections */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5" />
              Inspections
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <div className="text-center">
                <p className="text-xs text-muted-foreground mb-1">Total Inspections</p>
                <p className="text-2xl font-bold">{inspectionSummary?.totalInspections ?? "--"}</p>
                {inspectionSummary && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {Math.round(inspectionSummary.timeframeDays / 30)} months
                  </p>
                )}
              </div>
              <div className="text-center">
                <p className="text-xs text-muted-foreground mb-1">Vehicle Inspections</p>
                <p className="text-2xl font-bold">{inspectionSummary?.vehicleInspections ?? "--"}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-muted-foreground mb-1">Driver Inspections</p>
                <p className="text-2xl font-bold">{inspectionSummary?.driverInspections ?? "--"}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-muted-foreground mb-1">Vehicle OOS Rate</p>
                <p className={cn(
                  "text-2xl font-bold",
                  !inspectionSummary ? "" :
                  inspectionSummary.vehicleOosRate > 20 ? "text-red-600" : inspectionSummary.vehicleOosRate > 10 ? "text-yellow-600" : "text-green-600",
                )}>
                  {inspectionSummary ? `${inspectionSummary.vehicleOosRate.toFixed(1)}%` : "--"}
                </p>
              </div>
              <div className="text-center">
                <p className="text-xs text-muted-foreground mb-1">Driver OOS Rate</p>
                <p className={cn(
                  "text-2xl font-bold",
                  !inspectionSummary ? "" :
                  inspectionSummary.driverOosRate > 20 ? "text-red-600" : inspectionSummary.driverOosRate > 10 ? "text-yellow-600" : "text-green-600",
                )}>
                  {inspectionSummary ? `${inspectionSummary.driverOosRate.toFixed(1)}%` : "--"}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Violations Table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Violations
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {violations && violations.length > 0 ? (
              isMobile ? (
                <div className="divide-y">
                  {[...violations]
                    .sort((a, b) => b.count - a.count)
                    .map((v) => (
                      <div key={v.code} className="p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <p className="font-mono text-sm font-medium">{v.code}</p>
                            <p className="text-xs text-muted-foreground mt-0.5">{v.category}</p>
                          </div>
                          {v.oos && (
                            <Badge variant="outline" className="bg-red-100 text-red-800 border-red-200 text-xs">
                              OOS
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm mb-2">{v.description}</p>
                        <div className="grid grid-cols-2 gap-x-4 text-sm">
                          <div>
                            <span className="text-muted-foreground text-xs">Severity</span>
                            <p>{v.severity}</p>
                          </div>
                          <div>
                            <span className="text-muted-foreground text-xs">Count</span>
                            <p className="font-medium">{v.count}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Code</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead className="text-center">Severity</TableHead>
                        <TableHead className="text-center">Count</TableHead>
                        <TableHead className="text-center">OOS</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {[...violations]
                        .sort((a, b) => b.count - a.count)
                        .map((v) => (
                          <TableRow key={v.code}>
                            <TableCell className="font-mono">{v.code}</TableCell>
                            <TableCell>{v.description}</TableCell>
                            <TableCell className="text-muted-foreground">{v.category}</TableCell>
                            <TableCell className="text-center">{v.severity}</TableCell>
                            <TableCell className="text-center font-medium">{v.count}</TableCell>
                            <TableCell className="text-center">
                              {v.oos && (
                                <Badge variant="outline" className="bg-red-100 text-red-800 border-red-200 text-xs">
                                  OOS
                                </Badge>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </div>
              )
            ) : (
              <div className="p-6 text-center text-sm text-muted-foreground">
                No violations on record
              </div>
            )}
          </CardContent>
        </Card>

        {/* Crashes */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <CarFront className="w-5 h-5" />
              Crashes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <p className="text-xs text-muted-foreground mb-1">Total Crashes</p>
                <p className="text-2xl font-bold">{crashSummary?.totalCrashes ?? "--"}</p>
                {crashSummary && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {Math.round(crashSummary.timeframeDays / 30)} months
                  </p>
                )}
              </div>
              <div className="text-center">
                <p className="text-xs text-muted-foreground mb-1">Fatal</p>
                <p className={cn(
                  "text-2xl font-bold",
                  crashSummary && crashSummary.fatal > 0 ? "text-red-600" : "",
                )}>
                  {crashSummary?.fatal ?? "--"}
                </p>
              </div>
              <div className="text-center">
                <p className="text-xs text-muted-foreground mb-1">Injury</p>
                <p className={cn(
                  "text-2xl font-bold",
                  crashSummary && crashSummary.injury > 0 ? "text-yellow-600" : "",
                )}>
                  {crashSummary?.injury ?? "--"}
                </p>
              </div>
              <div className="text-center">
                <p className="text-xs text-muted-foreground mb-1">Towaway</p>
                <p className="text-2xl font-bold">{crashSummary?.towaway ?? "--"}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Change History */}
        {insuredProfile?.id && (
          <Card>
            <CardHeader className="pb-3 cursor-pointer" onClick={() => setChangeHistoryExpanded(!changeHistoryExpanded)}>
              <CardTitle className="text-lg flex items-center gap-2">
                <RotateCcw className="w-4 h-4" /> Change History
                {changeHistory.length > 0 && (
                  <Badge variant="secondary" className="text-xs ml-1">{changeHistory.length}</Badge>
                )}
                {changeHistory.length > 0 && (
                  changeHistoryExpanded
                    ? <ChevronUp className="w-4 h-4 ml-auto text-muted-foreground" />
                    : <ChevronDown className="w-4 h-4 ml-auto text-muted-foreground" />
                )}
              </CardTitle>
            </CardHeader>
            {(changeHistoryExpanded || changeHistory.length === 0) && (
              <CardContent>
                {changeHistory.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No changes recorded yet</p>
                ) : (
                  <div className="space-y-4">
                    {(() => {
                      const groups = new Map<string, InsuredChangeHistoryEntry[]>();
                      for (const entry of changeHistory) {
                        const key = entry.changedAt;
                        if (!groups.has(key)) groups.set(key, []);
                        groups.get(key)!.push(entry);
                      }
                      return Array.from(groups.entries()).map(([timestamp, entries]) => (
                        <div key={timestamp} className="border-l-2 border-muted pl-4 space-y-1">
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <span>{formatDate(timestamp)}</span>
                            {entries[0].changedByName && <span>by {entries[0].changedByName}</span>}
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                              {entries[0].source}
                            </Badge>
                          </div>
                          {entries[0].note && (
                            <p className="text-xs italic text-muted-foreground">{entries[0].note}</p>
                          )}
                          {entries.map((e) => (
                            <div key={e.id} className="text-sm">
                              <span className="font-medium">{insuredFieldLabel(e.field)}</span>:{" "}
                              <span className="text-muted-foreground line-through">{e.oldValue || "—"}</span>
                              {" → "}
                              <span className="font-medium">{e.newValue || "—"}</span>
                            </div>
                          ))}
                        </div>
                      ));
                    })()}
                  </div>
                )}
              </CardContent>
            )}
          </Card>
        )}

      </div>
    </Layout>
  );
}
